// HTTP client + mock helpers used by the service layer.
//
// Two flavours of fetch wrapper:
//   * mockResolve  — fixture path. Returns `value` after simulated latency.
//   * http<T>(path) — real backend path. The browser attaches voc_access +
//     voc_refresh httpOnly cookies on every request because credentials is
//     set to "include"; JS never sees the tokens. A 401 triggers ONE silent
//     refresh attempt (POST /auth/refresh with credentials → server reads
//     the refresh cookie, rotates, sets fresh cookies in the response) and
//     a single replay of the original request.

import { env } from "../config/env";
import { ApiError } from "../types";

/* ---------- mock helpers ---------- */

export function mockDelay(ms = env.mockLatencyMs): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function maybeFail(context: string): void {
  if (env.mockErrorRate > 0 && Math.random() < env.mockErrorRate) {
    throw new ApiError(`Mock failure: ${context}`, 503, "MOCK_ERROR");
  }
}

export async function mockResolve<T>(value: T, context: string): Promise<T> {
  await mockDelay();
  maybeFail(context);
  return structuredClone(value);
}

/* ---------- 401 hook (wired by the auth store) ---------- */

let _onUnauthorized: (() => void) | null = null;
export function onUnauthorized(cb: () => void): void {
  _onUnauthorized = cb;
}

/* ---------- shared fetch ---------- */

type RawInit = Omit<RequestInit, "headers"> & { headers?: Record<string, string> };

async function rawFetch<T>(url: string, init: RawInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers ?? {}),
  };
  const res = await fetch(url, { ...init, credentials: "include", headers });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const body = (await res.clone().json()) as { message?: string; detail?: string; code?: string };
      message = body.message ?? body.detail ?? message;
      code = body.code;
    } catch {
      /* non-JSON body — keep the statusText fallback */
    }
    throw new ApiError(message, res.status, code);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function refreshOnce(): Promise<boolean> {
  // POST /auth/refresh — the browser attaches the voc_refresh cookie via
  // credentials:"include" and the server reads it. No body needed today,
  // but we send an empty {} so the JSON content-type stays valid.
  try {
    const res = await fetch(`${env.authBaseUrl}/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function http<T>(path: string, init: RawInit = {}): Promise<T> {
  const url = `${env.apiBaseUrl}${path}`;
  try {
    return await rawFetch<T>(url, init);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      // One refresh attempt, then replay.
      const rotated = await refreshOnce();
      if (rotated) {
        try {
          return await rawFetch<T>(url, init);
        } catch (replayErr) {
          if (replayErr instanceof ApiError && replayErr.status === 401) {
            _onUnauthorized?.();
          }
          throw replayErr;
        }
      }
      _onUnauthorized?.();
    }
    throw err;
  }
}

/** Hit the public-auth surface (separate base, no refresh recursion). */
export async function auth<T>(path: string, init: RawInit = {}): Promise<T> {
  return rawFetch<T>(`${env.authBaseUrl}${path}`, init);
}
